// This route coordinates one Site Health audit report.
// It renders audit progress or the selected overview, page, or finding-type projection.
// It loads report projections only after the audit reaches a terminal state.
// It does not calculate findings, severity, scores, or remediation guidance.

import { motion } from 'motion/react'
import { useEffect, useRef } from 'react'
import { useParams, useSearchParams } from 'react-router'
import SiteHealthFindingTypeList from '@/components/site-health/SiteHealthFindingTypeList'
import SiteHealthOverview from '@/components/site-health/SiteHealthOverview'
import SiteHealthPageTable from '@/components/site-health/SiteHealthPageTable'
import SiteHealthProgress from '@/components/site-health/SiteHealthProgress'
import SiteHealthReportHeader from '@/components/site-health/SiteHealthReportHeader'
import SiteHealthReportTabs from '@/components/site-health/SiteHealthReportTabs'
import {
  ERROR_MESSAGE_CLASS,
  MESSAGE_CLASS,
} from '@/components/site-health/SiteHealthStyles'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent } from '@/components/ui/tabs'
import { useCursorPagination } from '@/hooks/useCursorPagination'
import { transitions } from '@/lib/motion/presets'
import { isSiteHealthView } from '@/lib/siteHealth'
import type { SiteHealthView } from '@/lib/siteHealth.types'
import {
  useSiteHealthAuditStatus,
  useSiteHealthFindingTypes,
  useSiteHealthOverview,
  useSiteHealthPages,
} from '@/lib/useSiteHealth'
import { useAnalytics } from '@/provider/useAuth'
import type {
  SiteHealthReportMessageProps,
  SiteHealthReportParams,
  SiteHealthReportProjectionProps,
} from './SiteHealthReport.types'

const MAIN_CLASS = 'mx-auto w-full max-w-7xl px-6 pb-20 pt-10'

function viewFromSearch(value: string | null): SiteHealthView {
  return value && isSiteHealthView(value) ? value : 'overview'
}

function isTerminal(status: string | undefined): boolean {
  return status === 'complete' || status === 'partial' || status === 'error'
}

function ReportMessage({
  children,
  error = false,
}: SiteHealthReportMessageProps) {
  return (
    <div className={error ? ERROR_MESSAGE_CLASS : MESSAGE_CLASS}>
      {children}
    </div>
  )
}

export default function SiteHealthReport() {
  const { auditId } = useParams<SiteHealthReportParams>()
  const [searchParams, setSearchParams] = useSearchParams()
  const pagePagination = useCursorPagination()
  const findingTypePagination = useCursorPagination()
  const analytics = useAnalytics()
  const reportViewedRef = useRef<string | null>(null)
  const reportTabViewedRef = useRef<Set<string>>(new Set())
  const activeView = viewFromSearch(searchParams.get('view'))
  const id = auditId ?? ''
  const statusQuery = useSiteHealthAuditStatus(id)
  const terminal = isTerminal(statusQuery.data?.status)
  const overviewQuery = useSiteHealthOverview(
    id,
    terminal && statusQuery.data?.status !== 'error'
  )
  const pagesQuery = useSiteHealthPages(
    id,
    pagePagination.cursor,
    terminal && activeView === 'pages'
  )
  const findingTypesQuery = useSiteHealthFindingTypes(
    id,
    findingTypePagination.cursor,
    terminal && activeView === 'finding-types'
  )

  useEffect(() => {
    if (!auditId || searchParams.get('view') === activeView) return

    const next = new URLSearchParams(searchParams)

    next.set('view', activeView)
    setSearchParams(next, { replace: true })
  }, [activeView, auditId, searchParams, setSearchParams])

  const selectedProjectionReady = Boolean(
    {
      overview: overviewQuery.data,
      pages: pagesQuery.data,
      'finding-types': findingTypesQuery.data,
    }[activeView]
  )

  useEffect(() => {
    if (!auditId || !selectedProjectionReady || !terminal) return

    if (reportViewedRef.current !== auditId) {
      analytics.track('site_health_report_viewed', {
        auditId,
        status: statusQuery.data?.status,
      })
      reportViewedRef.current = auditId
    }

    const tabKey = `${auditId}:${activeView}`

    if (!reportTabViewedRef.current.has(tabKey)) {
      analytics.track('site_health_report_tab_viewed', {
        auditId,
        view: activeView,
      })
      reportTabViewedRef.current.add(tabKey)
    }
  }, [
    activeView,
    analytics,
    auditId,
    selectedProjectionReady,
    statusQuery.data?.status,
    terminal,
  ])

  function resetPagination() {
    pagePagination.reset()
    findingTypePagination.reset()
  }

  if (!auditId) {
    return (
      <main className={MAIN_CLASS}>
        <ReportMessage error>Site Health audit ID is missing.</ReportMessage>
      </main>
    )
  }

  if (statusQuery.isPending) {
    return (
      <main className={MAIN_CLASS}>
        <ReportMessage>Loading Site Health audit…</ReportMessage>
      </main>
    )
  }

  if (statusQuery.isError || !statusQuery.data) {
    return (
      <main className={MAIN_CLASS}>
        <ReportMessage error>
          Site Health status could not be loaded. Refresh to try again.
        </ReportMessage>
      </main>
    )
  }

  if (
    statusQuery.data.status === 'pending' ||
    statusQuery.data.status === 'running'
  ) {
    return <SiteHealthProgress status={statusQuery.data} />
  }

  if (statusQuery.data.status === 'error') {
    return (
      <main className={MAIN_CLASS}>
        <ReportMessage error>
          {statusQuery.data.errorMessage ??
            'An internal error prevented a useful Site Health report.'}
        </ReportMessage>
      </main>
    )
  }

  return (
    <main className={MAIN_CLASS}>
      <Tabs value={activeView}>
        <motion.header
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={transitions.panel}
        >
          <SiteHealthReportHeader
            auditId={auditId}
            status={statusQuery.data}
            overview={overviewQuery.data}
          />
          <SiteHealthReportTabs
            activeView={activeView}
            auditId={auditId}
            onViewChange={resetPagination}
            className="mt-6"
          />
        </motion.header>

        <div className="mt-6">
          <TabsContent value="overview">
            {activeView === 'overview' && (
              <ReportProjection
                isPending={overviewQuery.isPending}
                isError={overviewQuery.isError}
                hasData={Boolean(overviewQuery.data)}
                errorMessage="Site Health report data could not be loaded. Refresh to try again."
              >
                {overviewQuery.data && (
                  <SiteHealthOverview overview={overviewQuery.data} />
                )}
              </ReportProjection>
            )}
          </TabsContent>

          <TabsContent value="pages">
            {activeView === 'pages' && (
              <ReportProjection
                isPending={pagesQuery.isPending}
                isError={pagesQuery.isError}
                hasData={Boolean(pagesQuery.data)}
                errorMessage="Page findings could not be loaded. Refresh to try again."
              >
                <SiteHealthPageTable
                  auditId={auditId}
                  pages={pagesQuery.data?.pages ?? []}
                  nextCursor={pagesQuery.data?.nextCursor ?? null}
                  onNextPage={() =>
                    pagePagination.next(pagesQuery.data?.nextCursor)
                  }
                  onPreviousPage={pagePagination.previous}
                  hasPreviousPage={pagePagination.hasPrevious}
                  onFiltersChange={pagePagination.reset}
                  onFindingExpanded={(findingId) =>
                    analytics.track('site_health_finding_expanded', {
                      auditId,
                      findingId,
                    })
                  }
                />
              </ReportProjection>
            )}
          </TabsContent>

          <TabsContent value="finding-types">
            {activeView === 'finding-types' && (
              <ReportProjection
                isPending={findingTypesQuery.isPending}
                isError={findingTypesQuery.isError}
                hasData={Boolean(findingTypesQuery.data)}
                errorMessage="Finding types could not be loaded. Refresh to try again."
              >
                <SiteHealthFindingTypeList
                  key={findingTypePagination.cursor ?? 'first-page'}
                  auditId={auditId}
                  findingTypes={findingTypesQuery.data?.findingTypes ?? []}
                />
                <div className="mt-4 flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={findingTypePagination.previous}
                    disabled={!findingTypePagination.hasPrevious}
                  >
                    Previous
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() =>
                      findingTypePagination.next(
                        findingTypesQuery.data?.nextCursor
                      )
                    }
                    disabled={!findingTypesQuery.data?.nextCursor}
                  >
                    Next page
                  </Button>
                </div>
              </ReportProjection>
            )}
          </TabsContent>
        </div>
      </Tabs>
    </main>
  )
}

function ReportProjection({
  children,
  isPending,
  isError,
  hasData,
  errorMessage,
}: SiteHealthReportProjectionProps) {
  if (isPending) return <ReportMessage>Loading report…</ReportMessage>

  if (isError || !hasData)
    return <ReportMessage error>{errorMessage}</ReportMessage>

  return children
}
